import dayjs from 'dayjs'

const $str = window.$str;
const lineColor = "#6A727C1A"; // 图表splitLine的颜色
const textColor = "#000000"; // 图表文字的颜色

const defaultMin = dayjs().format('YYYY-MM-DD 00:00:00');
const defaultMax = dayjs().format('YYYY-MM-DD 23:59:59');


export function getChartOption(chartData = [], start = defaultMin, end = defaultMax) {
  const { yMin, yMax } = getYaxisRange(chartData);

  const option = {
    backgroundColor: 'transparent',
    color: ['#1D77F5', '#FFC859', '#2AA523'],
    grid: [
      {
        top: '8%',
        left: '1%',
        right: '1%',
        bottom: '18%',
        containLabel: true,
        borderWidth: 0
      },
    ],
    xAxis: [
      {
        gridIndex: 0,
        name: $str.getKey('数据分析/时间'),
        type: 'time',
        nameLocation: 'end',
        min: start,
        max: end,
        boundaryGap: false,
        position: 'bottom',
        axisLine: {
          show: true,
          lineStyle: {
            color: lineColor,
            type: 'dashed'
          }
        },
        axisTick: {
          show: true
        },
        axisLabel: {
          color: textColor,
          show: true,
        },
        nameTextStyle: {
          color: textColor
        },
        axisPointer: {
          label: {
            show: true
          }
        }
      },
    ],
    yAxis: [
      {
        gridIndex: 0,
        name: $str.getKey('数据分析/电压(V)'),
        ...(yMin !== null && yMax !== null && isFinite(yMin) && isFinite(yMax) ? { min: yMin, max: yMax } : {}),
        nameLocation: 'end',
        nameGap: 12,
        nameTextStyle: {
          align: 'center',
          color: textColor,
          padding: [0, 0, 0, 45]
        },
        type: 'value',
        splitLine: {
          lineStyle: {
            color: '#eee',
            type: 'dashed'
          }
        },
        axisLabel: {
          formatter: function (value) {
            return Math.round(value).toString();
          },
          color: textColor,
        },
        axisLine: {
          show: false
        },
        splitNumber: 10
      },
    ],
    series: createSeries(...arguments),
    legend: (() => {
      const abVoltage = $str.getKey('数据分析/AB线电压');
      const bcVoltage = $str.getKey('数据分析/BC线电压');
      const caVoltage = $str.getKey('数据分析/CA线电压');
      return {
        data: [abVoltage, bcVoltage, caVoltage],
        bottom: 30,
        icon: 'circle',
        itemGap: 20,
        textStyle: {
          color: textColor
        },
        selected: {
          [abVoltage]: true,
          [bcVoltage]: true,
          [caVoltage]: true,
        }
      };
    })(),
    tooltip: createTooltip(),
    axisPointer: {
      link: [
        {
          xAxisIndex: [0, 1]
        }
      ],
      label: {
        backgroundColor: '#777'
      }
    },
    dataZoom: [
      {
        height: 12,
        bottom: 8,
        textStyle: {
          color: textColor
        },
        handleIcon: 'path://M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
        handleSize: '60%',
        dataBackground: {
          areaStyle: {
            color: '#8392A5'
          },
          lineStyle: {
            opacity: 0.8,
            color: '#8392A5'
          }
        },
        brushSelect: true,
        xAxisIndex: [0, 1]
      },
      {
        type: 'inside',
        xAxisIndex: [0, 1]
      }
    ],
    animation: false,
  }

  return option
}

function getYaxisRange(chartData) {
  const result = {
    yMin: null,
    yMax: null,
  }

  if (Array.isArray(chartData) && chartData.length > 0) {
    // 收集所有电压值（包括关口表和并网表的数据）
    const values = [];
    chartData.forEach(point => {
      if (point && typeof point === 'object') {
        const abValue = Number(point['u_ab']);
        const bcValue = Number(point['u_bc']);
        const caValue = Number(point['u_ca']);

        if (!isNaN(abValue) && isFinite(abValue)) values.push(abValue);
        if (!isNaN(bcValue) && isFinite(bcValue)) values.push(bcValue);
        if (!isNaN(caValue) && isFinite(caValue)) values.push(caValue);
      }
    });

    if (values.length > 0) {
      const minValue = Math.min(...values);
      const maxValue = Math.max(...values);

      // 计算范围，添加10%的边距，让图表显示更美观
      const range = maxValue - minValue;
      const padding = range * 0.1;

      result.yMin = minValue - padding;
      result.yMax = maxValue + padding;
    }
  }

  return result;
}


function createSeries(chartData) {
  const series = [];
  if (Array.isArray(chartData) && chartData.length > 0) {
    const abLineData = [];
    const bcLineData = [];
    const caLineData = [];

    chartData.forEach(point => {
      const time = point['record_time'];
      const abValue = Number(point['u_ab']).toFixed(2);
      const bcValue = Number(point['u_bc']).toFixed(2);
      const caValue = Number(point['u_ca']).toFixed(2);
      abLineData.push([time, abValue]);
      bcLineData.push([time, bcValue]);
      caLineData.push([time, caValue]);
    })

    const abSerie = {
      name: $str.getKey('数据分析/AB线电压'),
      type: 'line',
      showSymbol: false,
      smooth: true,
      lineStyle: {
        width: 1
      },
      data: abLineData
    }
    const bcSerie = {
      name: $str.getKey('数据分析/BC线电压'),
      type: 'line',
      showSymbol: false,
      smooth: true,
      lineStyle: {
        width: 1
      },
      data: bcLineData
    }
    const caSerie = {
      name: $str.getKey('数据分析/CA线电压'),
      type: 'line',
      showSymbol: false,
      smooth: true,
      lineStyle: {
        width: 1
      },
      data: caLineData
    }
    series.push(abSerie, bcSerie, caSerie);
  }

  return series;
}


function createTooltip(config = {}) {
  const timeFormatter = (value) => {
    // 格式化时间：如果是时间戳，转换为年月日时分秒；如果是时间字符串，直接使用
    let timeValue = value;
    if (typeof timeValue === 'number' || (typeof timeValue === 'string' && /^\d+$/.test(timeValue))) {
      // 判断是否是时间戳（毫秒或秒）
      const timestamp = Number(timeValue);
      const isMillisecond = timestamp > 946684800000; // 2000-01-01的时间戳（毫秒）
      timeValue = dayjs(isMillisecond ? timestamp : timestamp * 1000).format('YYYY-MM-DD HH:mm:ss');
    } else if (timeValue instanceof Date) {
      timeValue = dayjs(timeValue).format('YYYY-MM-DD HH:mm:ss');
    }
    return timeValue;
  };

  return {
    trigger: 'axis',
    axisPointer: {
      type: 'cross'
    },
    className: 'chart-tooltip',
    backgroundColor: 'rgba(255, 255, 255, 0.90)',
    borderWidth: 0,
    textStyle: {
      color: textColor
    },
    formatter: (params) => {
      let time = params[0].axisValue;
      time = timeFormatter(time);

      const str = (list) => {
        let t = '';
        for (const item of list) {
          const value = Array.isArray(item.data) ? item.data[1] : item.data;
          t += /* html */`
            <div class="chart-tooltip__series" style="display: flex; align-items: center; justify-content: space-between; gap: 8px; line-height: 1.5;">
              <div style="display: flex; align-items: center; gap: 6px; flex: 1;">
                <div class="chart-tooltip__point" style="background: ${item.color}; width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; display: inline-block; vertical-align: middle;"></div>
                <div class="chart-tooltip__series-name" style="display: inline-block; vertical-align: middle;">${item.seriesName}</div>
              </div>
              <div class="chart-tooltip__value" style="flex-shrink: 0; display: inline-block; vertical-align: middle; text-align: right;">${value ?? '--'}</div>
            </div>`;
        }
        return t;
      };

      return /* html */`
        <div class="chart-tooltip__time" style="margin-bottom: 12px;">${time}</div>
        <div class="chart-tooltip__items-container">${str(params)}</div>        
      `;
    }
  }
}


